import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { doc, onSnapshot, setDoc, updateDoc, arrayUnion, increment, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '../firebase-config';
import { MODULES } from '../data/curriculum';

const ADMIN_EMAILS = [
  'admin@theoperatormethod.com',
  'rmcmanus@ramdesignworks.com',
  'r0dmac522@gmail.com',
];

function isAdminEmail(email) {
  const normalized = String(email || '').trim().toLowerCase();
  return ADMIN_EMAILS.includes(normalized);
}

const FLAG_THRESHOLD = 3; // flag account after this many IP changes
const DEFAULT_INACTIVITY_LOGOUT_HOURS = 4;
const MIN_INACTIVITY_LOGOUT_HOURS = 1;
const MAX_INACTIVITY_LOGOUT_HOURS = 24;

const AuthContext = createContext(null);

// Tier → which modules are accessible
export const TIER_ACCESS = {
  foundations: ['module-0', 'module-1'],
  operator:    ['module-0', 'module-1', 'module-2', 'module-3', 'module-4'],
  certified:   ['module-0', 'module-1', 'module-2', 'module-3', 'module-4'],
  // Beta testers get full access without payment
  beta:        ['module-0', 'module-1', 'module-2', 'module-3', 'module-4'],
};

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [inactivityLogoutHours, setInactivityLogoutHours] = useState(DEFAULT_INACTIVITY_LOGOUT_HOURS);
  const [loading, setLoading] = useState(true);
  // IP verification state
  const [pendingIPVerification, setPendingIPVerification] = useState(null); // { newIP } | null
  const ipCheckedRef = useRef(false); // only run IP check once per auth session

  async function fetchIP() {
    try {
      const res = await fetch('https://api.ipify.org?format=json');
      const data = await res.json();
      return data.ip;
    } catch {
      return null; // don't block access if IP fetch fails
    }
  }

  async function checkIPForUser(user, profile) {
    if (isAdminEmail(user.email)) return; // skip for admin

    // Skip IP check for brand new accounts (created within the last 2 minutes).
    // New signups have no prior IP baseline and should never see a verification challenge.
    const createdAt = profile?.createdAt?.toMillis?.() || 0;
    if (createdAt && Date.now() - createdAt < 2 * 60 * 1000) return;

    const currentIP = await fetchIP();
    if (!currentIP) return; // can't determine IP — don't block

    const userRef = doc(db, 'users', user.uid);

    if (!profile?.currentIP) {
      // First session — store IP silently, no challenge
      await setDoc(userRef, {
        currentIP,
        ipHistory: [{ ip: currentIP, ua: navigator.userAgent.slice(0, 150), ts: Date.now() }],
        ipChangeCount: 0,
      }, { merge: true });
    } else if (profile.currentIP !== currentIP) {
      // IP changed — trigger verification modal
      setPendingIPVerification({ newIP: currentIP });
    }
  }

  // Called by IPVerificationModal after successful re-auth
  function clearPendingIPVerification() {
    setPendingIPVerification(null);
  }

  useEffect(() => {
    let profileUnsub = () => {};

    // Safety: if Firebase auth hasn't resolved in 5 seconds, unblock the UI
    const loadingTimeout = setTimeout(() => setLoading(false), 5000);

    const authUnsub = onAuthStateChanged(auth, async (user) => {
      clearTimeout(loadingTimeout);
      ipCheckedRef.current = false; // reset check on every auth change

      if (user) {
        // browserSessionPersistence in firebase-config handles tab-scoping natively.
        // No stale-session guard needed here.
        setCurrentUser(user);
        // Record last login time (fire-and-forget — don't block on it)
        setDoc(doc(db, 'users', user.uid), { lastLoginAt: serverTimestamp() }, { merge: true }).catch(() => {});

        profileUnsub = onSnapshot(doc(db, 'users', user.uid), (snap) => {
          const data = snap.exists() ? snap.data() : {};
          setUserProfile(data);
          setLoading(false);

          // Run IP check exactly once per session (first time profile arrives)
          if (!ipCheckedRef.current) {
            ipCheckedRef.current = true;
            checkIPForUser(user, data);
          }
        });
      } else {
        setCurrentUser(null);
        profileUnsub();
        setUserProfile(null);
        setPendingIPVerification(null);
        setLoading(false);
      }
    });

    return () => { clearTimeout(loadingTimeout); authUnsub(); profileUnsub(); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Heartbeat — write lastSeen every 2 min while logged in (feeds Admin Active Now)
  useEffect(() => {
    if (!currentUser) return;
    const update = () =>
      setDoc(doc(db, 'users', currentUser.uid), { lastSeen: serverTimestamp() }, { merge: true }).catch(() => {});
    update();
    const iv = setInterval(update, 2 * 60 * 1000);
    return () => clearInterval(iv);
  }, [currentUser?.uid]); // eslint-disable-line react-hooks/exhaustive-deps

  // Global session policy config
  useEffect(() => {
    const unsub = onSnapshot(doc(db, 'app_config', 'session_policy'), (snap) => {
      const raw = snap.data()?.inactivityLogoutHours;
      const parsed = Number(raw);
      if (Number.isFinite(parsed)) {
        const bounded = Math.min(MAX_INACTIVITY_LOGOUT_HOURS, Math.max(MIN_INACTIVITY_LOGOUT_HOURS, parsed));
        setInactivityLogoutHours(bounded);
      } else {
        setInactivityLogoutHours(DEFAULT_INACTIVITY_LOGOUT_HOURS);
      }
    }, () => {
      setInactivityLogoutHours(DEFAULT_INACTIVITY_LOGOUT_HOURS);
    });
    return unsub;
  }, []);

  // Auto-logout after X hours of inactivity to avoid stale auto-login sessions.
  useEffect(() => {
    if (!currentUser) return;

    let timeoutId;
    let isSigningOut = false;

    const events = [
      'mousedown',
      'mousemove',
      'keydown',
      'scroll',
      'touchstart',
      'click',
    ];

    const clearExistingTimeout = () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };

    const armTimeout = () => {
      clearExistingTimeout();
      const inactivityLogoutMs = inactivityLogoutHours * 60 * 60 * 1000;
      timeoutId = setTimeout(async () => {
        if (isSigningOut) return;
        isSigningOut = true;
        try {
          // Mark as intentional signout so existing auth guards route to /login.
          sessionStorage.setItem('om_intentional_signout', '1');
          await signOut(auth);
        } finally {
          isSigningOut = false;
        }
      }, inactivityLogoutMs);
    };

    const onActivity = () => {
      armTimeout();
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        armTimeout();
      }
    };

    events.forEach((eventName) => window.addEventListener(eventName, onActivity));
    document.addEventListener('visibilitychange', onVisibilityChange);
    armTimeout();

    return () => {
      clearExistingTimeout();
      events.forEach((eventName) => window.removeEventListener(eventName, onActivity));
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [currentUser?.uid, inactivityLogoutHours]);

  // Convenience: does the signed-in user have access to a given moduleId?
  // Checks both tier access AND sequential progression (must complete previous module first).
  function canAccessModule(moduleId) {
    if (isAdminEmail(currentUser?.email)) return true; // admin bypasses all gates
    if (!userProfile?.tier) return false;
    // Tier gate — is this module in the user's plan?
    if (!(TIER_ACCESS[userProfile.tier] || []).includes(moduleId)) return false;
    // Progression gate — is the previous module fully completed?
    const sorted = [...MODULES].sort((a, b) => a.order - b.order);
    const idx = sorted.findIndex(m => m.id === moduleId);
    if (idx <= 0) return true; // module-0 has no predecessor
    const prevMod = sorted[idx - 1];
    if (!prevMod.gated) {
      // Previous module is orientation (not gated) — just need it completed
      const prog = userProfile?.progress || {};
      return prevMod.lessons.every(l => prog?.[prevMod.id]?.[l.id]?.completed === true);
    }
    // Previous module is gated — all its lessons must be completed
    const prog = userProfile?.progress || {};
    return prevMod.lessons.every(l => prog?.[prevMod.id]?.[l.id]?.completed === true);
  }

  const isAdmin = isAdminEmail(currentUser?.email);

  return (
    <AuthContext.Provider value={{
      currentUser, userProfile, loading, canAccessModule, isAdmin,
      pendingIPVerification, clearPendingIPVerification,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
